import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Form, Input, InputNumber, Select, DatePicker, Button, Space, Upload, Collapse,
  Checkbox, Row, Col, Alert, App as AntdApp, Rate,
} from 'antd';
import { ArrowLeft, Upload as UploadIcon, Briefcase } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { taskService } from '@/services/taskService';
import { userService } from '@/services/userService';
import { createCategoryService } from '@/services/categoryService';
import { PageHeader } from '@/components/common/PageHeader';
import { RichEditor, type RichEditorValue } from '@/components/common/RichEditor';
import { parseDescription, serializeDescription } from '@/utils/richContent';
import { useAuthStore } from '@/store/authStore';
import { StarLevelRate, URGENT_TOOLTIPS, IMPORTANT_TOOLTIPS } from '@/components/common/StarLevelRate';  // V1.0.52
// import type { User } from '@/types'; // V1.0.47: không còn dùng direct sau khi chuyển useQuery

const { TextArea } = Input;

const deptService = createCategoryService<any>('departments');
// V1.0.49: Tags được quản lý qua danh mục labels
const labelService = createCategoryService<any>('labels');

/**
 * Form.Item wrapper - lưu description dưới dạng JSON string {html, attachments}
 * để tương thích với column `description TEXT` sẵn có trong DB.
 */
function RichEditorFormField({
  value, onChange, placeholder,
}: { value?: string; onChange?: (v: string) => void; placeholder?: string }) {
  const parsed = parseDescription(value);
  return (
    <RichEditor
      value={parsed}
      onChange={(v: RichEditorValue) => onChange?.(serializeDescription(v))}
      placeholder={placeholder}
      minHeight={100}
      showAttachments={false}
    />
  );
}

export function TaskFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();
  const isEdit = !!id;
  const currentUser = useAuthStore(s => s.user);

  // Pre-fill khi navigate từ ProjectsPage: { state: { projectId, projectName, departmentId } }
  const presetProjectId: string | undefined = location.state?.projectId;
  const presetProjectName: string | undefined = location.state?.projectName;
  const presetDepartmentId: string | undefined = location.state?.departmentId;

  // Permission: BOD/SYS_ADMIN/DEPT_HEAD được chọn bất kỳ khoa phòng nào
  // User thường thuộc nhiều dept: chọn được trong list dept mình
  const isAdminOrBod = !!currentUser?.roles?.some(
    r => r === 'SYS_ADMIN' || r === 'BOD' || r === 'DEPT_HEAD'
  );
  // V1.0.24: Lấy list department user thuộc (có thể nhiều)
  const userDepts = (currentUser as any)?.departments as Array<{ departmentId: string; departmentName: string }> | undefined;
  const hasMultipleDepts = (userDepts?.length ?? 0) > 1;
  const canPickAnyDept = isAdminOrBod || hasMultipleDepts;

  // Khoa phòng default cho user thường:
  // - Nếu có preset từ project → ưu tiên (đã có quyền vào project tức được phép)
  // - Nếu không → primary department của user
  const defaultDepartmentId = presetDepartmentId ?? currentUser?.department?.id;

  // Watch departmentId để filter assignee
  const watchedDepartmentId = Form.useWatch('departmentId', form);

  // V1.0.47: Dùng useQuery thay useEffect để invalidate khi data đổi (giải quyết stale cache)
  const { data: usersResp, isLoading: usersLoading } = useQuery({
    queryKey: ['users-task-form'],
    queryFn: () => userService.list({ pageSize: 1000 }),
  });
  const { data: depts = [], isLoading: deptsLoading } = useQuery({
    queryKey: ['departments-list'],  // V1.0.44: cùng key với CategoryPages invalidate
    queryFn: () => deptService.list().catch(() => [] as any[]),
  });
  // V1.0.49: Load tags từ danh mục labels
  const { data: labelsList = [] } = useQuery({
    queryKey: ['labels-list'],
    queryFn: () => labelService.list().catch(() => [] as any[]),
  });
  const labelOptions = (labelsList as any[])
    .filter((l: any) => l.isActive !== false)
    .map((l: any) => ({
      value: l.code ?? l.name,  // dùng code làm value, fallback name
      label: l.name,
    }));
  const users = usersResp?.items ?? [];
  const departments = depts;
  const loadingOpts = usersLoading || deptsLoading;

  // V1.0.25: file đính kèm chờ upload sau khi task được tạo
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const createMutation = useMutation({
    mutationFn: taskService.create,
    onSuccess: async (newTask: any) => {
      // V1.0.25: upload các file đã chọn vào task vừa tạo
      if (pendingFiles.length > 0 && newTask?.id) {
        message.loading({ content: `Đang upload ${pendingFiles.length} file...`, key: 'upload', duration: 0 });
        let succeeded = 0;
        for (const f of pendingFiles) {
          try {
            await taskService.uploadAttachment(newTask.id, f);
            succeeded++;
          } catch (e) {
            console.error('Upload failed for', f.name, e);
          }
        }
        message.success({
          content: `Đã tạo task + upload ${succeeded}/${pendingFiles.length} file`,
          key: 'upload',
        });
      } else {
        message.success('Đã tạo task thành công');
      }

      qc.invalidateQueries({ queryKey: ['tasks'] });
      if (presetProjectId) {
        qc.invalidateQueries({ queryKey: ['project-tasks', presetProjectId] });
        qc.invalidateQueries({ queryKey: ['projects'] });
        navigate('/projects', { state: { selectProjectId: presetProjectId } });
      } else {
        navigate('/tasks');
      }
    },
    onError: (err: any) => {
      console.error('[TaskForm] Create failed:', err);
      console.error('[TaskForm] Response:', err.response?.data);
      const errors = err.response?.data?.errors;
      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];
        const firstMsg = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];
        message.error(`Lỗi: ${firstMsg}`);
      } else {
        message.error(err.response?.data?.message ?? err.message ?? 'Tạo task thất bại');
      }
    },
  });

  const onFinish = (values: any) => {
    const payload: any = {
      ...values,
      dueDate: values.dueDate?.format('YYYY-MM-DD'),
      startDate: values.startDate?.format('YYYY-MM-DD'),
    };

    // V1.0.54: Người thực hiện chỉ 1 người (Select single mode) → wrap thành array cho BE
    // BE field CreateTaskRequest.AssigneeIds vẫn là IReadOnlyList<Guid> (giữ compat ngược)
    if (values.assigneeIds && !Array.isArray(values.assigneeIds)) {
      payload.assigneeIds = [values.assigneeIds];
    } else if (Array.isArray(values.assigneeIds)) {
      // Trường hợp form load với array (edit task cũ có nhiều assignee): lấy phần tử đầu
      payload.assigneeIds = values.assigneeIds.length > 0 ? [values.assigneeIds[0]] : [];
    }

    // V1.0.26: derive priority enum cũ (LOW/MEDIUM/HIGH/CRITICAL) cho BE compat
    // Lấy max của Gấp + Quan trọng làm priority chính
    const maxLvl = Math.max(values.priorityLevel ?? 3, values.importantLevel ?? 3);
    if (maxLvl >= 5) payload.priority = 'CRITICAL';
    else if (maxLvl >= 4) payload.priority = 'HIGH';
    else if (maxLvl >= 3) payload.priority = 'MEDIUM';
    else payload.priority = 'LOW';

    // Inject projectId nếu form được mở từ ProjectsPage (qua navigate state)
    if (presetProjectId) {
      payload.projectId = presetProjectId;
    }

    // Build recurrence object nếu user chọn lặp
    const freq = values.recurrenceFrequency as 'NONE' | 'WEEKLY' | 'MONTHLY' | undefined;
    if (freq === 'WEEKLY' || freq === 'MONTHLY') {
      payload.recurrence = {
        frequency: freq,
        daysOfWeek: freq === 'WEEKLY' ? values.recurrenceDaysOfWeek : undefined,
        daysOfMonth: freq === 'MONTHLY' ? values.recurrenceDaysOfMonth : undefined,
        endDate: values.recurrenceEndDate?.format('YYYY-MM-DD'),
        durationDays: values.recurrenceDurationDays ?? 1,
      };
    }
    // Remove các field phụ không thuộc CreateTaskRequest
    delete payload.recurrenceFrequency;
    delete payload.recurrenceDaysOfWeek;
    delete payload.recurrenceDaysOfMonth;
    delete payload.recurrenceEndDate;
    delete payload.recurrenceDurationDays;

    console.log('[TaskForm] Submit payload:', payload);
    createMutation.mutate(payload);
  };

  // Khi BOD/admin đổi khoa phòng → clear assignee/watcher đã chọn (vì user không thuộc dept mới)
  // Chỉ chạy khi watchedDepartmentId thay đổi sau lần load đầu tiên
  const [previousDeptId, setPreviousDeptId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!watchedDepartmentId) return;
    if (previousDeptId === undefined) {
      // Lần đầu set, không clear
      setPreviousDeptId(watchedDepartmentId);
      return;
    }
    if (previousDeptId !== watchedDepartmentId) {
      // Dept thay đổi → clear assigneeIds (V1.0.54: single mode → set undefined thay vì [])
      form.setFieldsValue({ assigneeIds: undefined });
      setPreviousDeptId(watchedDepartmentId);
    }
  }, [watchedDepartmentId, previousDeptId, form]);

  // userOptions: filter theo department được chọn (chỉ cho Người thực hiện)
  // V1.0.24: User có thể thuộc nhiều phòng ban → check cả departments[] (nếu có) lẫn primary
  const userOptions = users
    .filter(u => {
      if (!watchedDepartmentId) return false;  // Buộc chọn khoa trước
      // Check trong departments[] (V1.0.24)
      const inSecondary = (u as any).departments?.some(
        (d: any) => d.departmentId === watchedDepartmentId
      );
      // Fallback: check primary
      const inPrimary = u.department?.id === watchedDepartmentId;
      return inSecondary || inPrimary;
    })
    .map(u => ({
      value: u.id,
      label: `${u.fullName} — ${u.position ?? ''}`,
    }));

  // V1.0.45: User toàn viện (cho Watcher + Collaborator + Informed — không filter theo khoa)
  const allUserOptions = users.map(u => ({
    value: u.id,
    label: `${u.fullName}${u.department?.name ? ` — ${u.department.name}` : ''}`,
  }));

  return (
    <>
      <PageHeader
        breadcrumb={isEdit ? 'CHỈNH SỬA TASK' : 'TẠO TASK MỚI'}
        title={isEdit ? 'Chỉnh sửa task' : 'Giao công việc'}
        extra={
          <Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
            Hủy
          </Button>
        }
      />

      {/* Banner báo task thuộc project nếu có */}
      {!isEdit && presetProjectId && (
        <div style={{ maxWidth: 880, margin: '0 auto 12px' }}>
          <Alert
            type="info"
            showIcon
            icon={<Briefcase size={16} />}
            message={
              <span>
                Task này sẽ được gắn vào dự án: <strong>{presetProjectName ?? 'Dự án đã chọn'}</strong>
              </span>
            }
          />
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e1e5eb', borderRadius: 8, padding: 24, maxWidth: 880, margin: '0 auto' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            category: 'TECHNICAL',
            departmentId: defaultDepartmentId,
            priorityLevel: 3,    // Cấp Gấp mặc định
            importantLevel: 3,   // Cấp Quan trọng mặc định
          }}
          requiredMark
          disabled={loadingOpts}
        >
          <Form.Item
            label="Tiêu đề"
            name="title"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
          >
            <Input placeholder="Ví dụ: Hoàn thiện báo cáo bảo mật quý 2" size="large" />
          </Form.Item>

          <Form.Item label="Mô tả chi tiết" name="description">
            <RichEditorFormField placeholder="Nhập mô tả task, có thể format text + chèn hyperlink..." />
          </Form.Item>

          {/* Khoa/Phòng + Phân loại — chọn trước để filter assignee */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  canPickAnyDept
                    ? "Khoa/Phòng"
                    : <span>Khoa/Phòng <span style={{ color: '#7d8a98', fontWeight: 'normal' }}>(theo phòng ban của bạn)</span></span>
                }
                name="departmentId"
                rules={[{ required: true, message: 'Bắt buộc chọn khoa/phòng' }]}
                extra={canPickAnyDept ? "Đổi khoa sẽ reset danh sách người thực hiện" : undefined}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn khoa/phòng..."
                  loading={loadingOpts}
                  disabled={!canPickAnyDept}
                  options={(() => {
                    // V1.0.47: Filter ra khoa active (isActive !== false) — ẩn khoa đã tắt khỏi UI tạo task
                    const activeDepts = departments.filter((d: any) => d.isActive !== false);
                    if (isAdminOrBod) {
                      return activeDepts.map((d: any) => ({
                        value: d.id,
                        label: `${d.code} · ${d.name}`,
                      }));
                    }
                    const userDeptIds = new Set(userDepts?.map(d => d.departmentId) ?? []);
                    if (currentUser?.department?.id) userDeptIds.add(currentUser.department.id);
                    return activeDepts
                      .filter((d: any) => userDeptIds.has(d.id))
                      .map((d: any) => ({
                        value: d.id,
                        label: `${d.code} · ${d.name}`,
                      }));
                  })()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Phân loại" name="category"
                rules={[{ required: true }]}>
                <Select options={[
                  { value: 'ADMIN', label: 'Hành chính' },
                  { value: 'TECHNICAL', label: 'Kỹ thuật' },
                  { value: 'MEDICAL', label: 'Y tế' },
                  { value: 'OTHER', label: 'Khác' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          {/* V1.0.26: 2 trục Eisenhower — Gấp + Quan trọng, mỗi trục cấp 1-5 */}
          {/* V1.0.44: Dùng Rate (ngôi sao) thay Select cho UI trực quan hơn */}
          {/* V1.0.52: Gradient màu vàng → đỏ theo cấp 1 → 5 (StarLevelRate) */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span><span style={{ color: '#f5222d', marginRight: 4 }}>🔴</span> Mức độ Gấp</span>}
                name="priorityLevel"
                tooltip="Càng nhiều sao = càng gấp. 5 sao = Rất gấp (đỏ), 1 sao = Không gấp (vàng)."
                rules={[{ required: true, message: 'Chọn cấp độ gấp' }]}
              >
                <StarLevelRate tooltips={URGENT_TOOLTIPS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span><span style={{ color: '#fa8c16', marginRight: 4 }}>🟠</span> Mức độ Quan trọng</span>}
                name="importantLevel"
                tooltip="Càng nhiều sao = càng quan trọng. 5 sao = Rất quan trọng (đỏ), 1 sao = Không quan trọng (vàng)."
                rules={[{ required: true, message: 'Chọn cấp độ quan trọng' }]}
              >
                <StarLevelRate tooltips={IMPORTANT_TOOLTIPS} />
              </Form.Item>
            </Col>
          </Row>

          {/* Bắt đầu + Hạn + Ước tính */}
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Bắt đầu" name="startDate">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Hạn"
                name="dueDate"
                rules={[{ required: true, message: 'Bắt buộc' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Thời gian ước tính (giờ)" name="estimatedHours">
                <Input type="number" placeholder="VD: 8" />
              </Form.Item>
            </Col>
          </Row>

          {/* V1.0.30: KPI Doanh thu */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="💰 KPI Doanh thu kế hoạch (VND)"
                name="kpiRevenuePlan"
                extra="Không bắt buộc. Nếu đặt > 0, khi đề nghị đóng task sẽ yêu cầu nhập doanh thu thực tế."
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1000000}
                  placeholder="VD: 100,000,000"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={((value: string | undefined) => Number(value?.replace(/\D/g, '') ?? 0)) as any}
                  addonAfter="đ"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Người thực hiện + Người theo dõi - sau khi đã chọn khoa */}
          {/* V1.0.54: Người thực hiện chỉ chọn 1 người (đổi từ multiple → single) */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Người thực hiện (Chính)"
                name="assigneeIds"
                rules={[{ required: true, message: 'Chọn người thực hiện' }]}
                extra={!watchedDepartmentId ? "Chọn khoa/phòng trước" : `${userOptions.length} người trong khoa`}
                tooltip="V1.0.54: Mỗi task chỉ có 1 người thực hiện chính. Nếu cần phân chia, hãy tạo task con."
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={watchedDepartmentId ? "Chọn 1 người..." : "Chọn khoa/phòng trước..."}
                  loading={loadingOpts}
                  disabled={!watchedDepartmentId}
                  options={userOptions}
                  notFoundContent={watchedDepartmentId ? "Không có nhân sự trong khoa này" : null}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/* V1.0.45: Người theo dõi không filter theo khoa nữa — chọn toàn viện */}
              <Form.Item
                label="Người theo dõi"
                name="watcherIds"
                extra="Người được thông báo tiến độ, không có trách nhiệm thực hiện."
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn người theo dõi (toàn viện)..."
                  loading={loadingOpts}
                  options={allUserOptions}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* V1.0.45: 2 vai trò mới — xuyên khoa, không phụ thuộc dept */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Người phối hợp"
                name="collaboratorIds"
                extra="Cùng tham gia thực hiện hoặc hỗ trợ. Có thể bình luận. KHÔNG phụ thuộc khoa/phòng."
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn người phối hợp (toàn viện)..."
                  loading={loadingOpts}
                  options={allUserOptions}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Người cung cấp thông tin"
                name="informedIds"
                extra="Chỉ xem để biết kết quả, không tham gia. KHÔNG phụ thuộc khoa/phòng."
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn người cung cấp thông tin (toàn viện)..."
                  loading={loadingOpts}
                  options={allUserOptions}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* V1.0.49: Tags từ danh mục labels - autocomplete, vẫn cho phép thêm mới */}
          <Form.Item
            label="Nhãn (tags)"
            name="tags"
            extra="Chọn từ danh mục Nhãn / Tag (quản trị > Nhãn). Có thể gõ mới và Enter để thêm."
          >
            <Select
              mode="tags"
              placeholder="Chọn nhãn từ danh mục..."
              options={labelOptions}
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Form.Item>

          {/* ======== LẶP LẠI ======== */}
          <Form.Item label="Lặp lại" name="recurrenceFrequency"
            initialValue="NONE"
            extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
              Nếu chọn lặp, task gốc là template. Hệ thống sẽ tự sinh task con mỗi khi đến kỳ (mỗi tuần/tháng).
            </div>}>
            <Select
              options={[
                { value: 'NONE', label: 'Không lặp (task 1 lần)' },
                { value: 'WEEKLY', label: 'Hằng tuần' },
                { value: 'MONTHLY', label: 'Hằng tháng' },
              ]}
            />
          </Form.Item>

          <Form.Item shouldUpdate={(prev, cur) => prev.recurrenceFrequency !== cur.recurrenceFrequency} noStyle>
            {({ getFieldValue }) => {
              const freq = getFieldValue('recurrenceFrequency');
              if (freq === 'WEEKLY') {
                return (
                  <Row gutter={16}>
                    <Col xs={24} md={14}>
                      <Form.Item label="Các thứ trong tuần" name="recurrenceDaysOfWeek"
                        rules={[{ required: true, message: 'Chọn ít nhất 1 thứ' }]}>
                        <Checkbox.Group
                          options={[
                            { label: 'T2', value: 1 },
                            { label: 'T3', value: 2 },
                            { label: 'T4', value: 3 },
                            { label: 'T5', value: 4 },
                            { label: 'T6', value: 5 },
                            { label: 'T7', value: 6 },
                            { label: 'CN', value: 7 },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={10}>
                      <Form.Item label="Ngày kết thúc (tùy chọn)" name="recurrenceEndDate">
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Để trống = lặp vô hạn" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={14}>
                      <Form.Item label="Số ngày hoàn thành cho mỗi task con" name="recurrenceDurationDays"
                        initialValue={1}
                        extra="Task con sẽ có due_date = ngày sinh + số ngày này">
                        <Select options={[1, 2, 3, 5, 7, 14].map(n => ({ value: n, label: `${n} ngày` }))} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              if (freq === 'MONTHLY') {
                return (
                  <Row gutter={16}>
                    <Col xs={24} md={14}>
                      <Form.Item label="Các ngày trong tháng" name="recurrenceDaysOfMonth"
                        rules={[{ required: true, message: 'Chọn ít nhất 1 ngày' }]}
                        extra={<div style={{ fontSize: 11, color: '#8a95a5' }}>
                          VD: [1, 15] = ngày 1 và 15 mỗi tháng. Nếu tháng đó không có ngày đó (VD: 31/2) sẽ bỏ qua.
                        </div>}>
                        <Select mode="multiple" placeholder="Chọn ngày..."
                          options={Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: `Ngày ${i + 1}` }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={10}>
                      <Form.Item label="Ngày kết thúc (tùy chọn)" name="recurrenceEndDate">
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Để trống = lặp vô hạn" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={14}>
                      <Form.Item label="Số ngày hoàn thành cho mỗi task con" name="recurrenceDurationDays"
                        initialValue={3}>
                        <Select options={[1, 3, 5, 7, 14, 30].map(n => ({ value: n, label: `${n} ngày` }))} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item label="Tài liệu đính kèm"
            extra={
              <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                💡 File sẽ được upload vào task ngay sau khi tạo. Tối đa 50MB/file.
              </div>
            }>
            <Upload.Dragger
              multiple
              beforeUpload={(file) => {
                // Limit 50MB
                if (file.size > 50 * 1024 * 1024) {
                  message.error(`File "${file.name}" vượt quá 50MB`);
                  return Upload.LIST_IGNORE;
                }
                setPendingFiles(prev => [...prev, file]);
                return false;  // Không upload ngay - chờ submit task
              }}
              onRemove={(file: any) => {
                setPendingFiles(prev => prev.filter(f => f.name !== file.name || f.size !== file.size));
                return true;
              }}
              fileList={pendingFiles.map((f, i) => ({
                uid: `pending-${i}`,
                name: f.name,
                size: f.size,
                status: 'done' as const,
              }))}
            >
              <div style={{ padding: 20 }}>
                <UploadIcon size={32} style={{ color: '#1976D2' }} />
                <div style={{ marginTop: 10, fontSize: 13, fontWeight: 500 }}>
                  Kéo thả file vào đây hoặc <span style={{ color: '#1976D2' }}>click để chọn</span>
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#8a95a5' }}>
                  Hỗ trợ tất cả định dạng — File đã chọn: <strong>{pendingFiles.length}</strong>
                </div>
              </div>
            </Upload.Dragger>
          </Form.Item>

          <Collapse
            ghost
            items={[
              {
                key: 'advanced',
                label: 'Tùy chọn nâng cao',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item name="requiresSignature" valuePropName="checked" noStyle>
                      <Checkbox>Yêu cầu ký điện tử sau khi hoàn thành</Checkbox>
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />

          <Form.Item style={{ marginTop: 20, marginBottom: 0, borderTop: '1px solid #e1e5eb', paddingTop: 20 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => navigate(-1)}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
                {isEdit ? 'Lưu thay đổi' : 'Giao task'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}
